import type { BulkAction, BulkOperationSummary, ODataBulkOperation } from "../../api/types.ts";
import { bulkSummaryQueryOptions } from "../../api/routes/remediation/bulk.ts";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { bulkOperationsQueryOptions } from "../../api/routes/odata/bulk-operations.ts";
import type { RemediationSelection } from "./RemediationSelection.ts";
import { useRunningSelections } from "./useRunningSelections.ts";

function combineBulkSummaries(results: { data?: BulkOperationSummary }[]) {
    return results.map(result => result.data).filter((data): data is BulkOperationSummary => data !== undefined);
}

export function useRemediationBulkSummariesQuery(bulkIds: readonly string[]) {
    const key = bulkIds.join(",");
    const queries = useMemo(() => (key ? key.split(",").map(bulkSummaryQueryOptions) : []), [key]);

    return useQueries({ queries, combine: combineBulkSummaries });
}

export interface RunningOperation {
    bulkId: string;
    action: BulkAction;
    isPolicy: boolean;
    createdAt: string;
    targetDisplayName: string | null;
}

type FailedStatus = FailedOperation["status"];

export interface FailedOperation {
    bulkId: string;
    action: BulkAction;
    isPolicy: boolean;
    status: "failure" | "partialFailure";
    succeededCount: number;
    failedCount: number;
    errorMessage: string | null;
}

interface BulkOperationLists {
    inProgressIds: string[];
    runningOperations: RunningOperation[];
    failedOperations: FailedOperation[];
}

function isFailed(operation: ODataBulkOperation): operation is ODataBulkOperation & { status: FailedStatus } {
    return operation.status === "failure" || operation.status === "partialFailure";
}

export interface BulkProps {
    summaries: BulkOperationSummary[];
    runningOperations: RunningOperation[];
    failedOperations: FailedOperation[];
    inProgressCount: number;
    isSelectionRunning: (selection: RemediationSelection) => boolean;
}

export const emptyBulkContext: BulkProps = {
    summaries: [],
    runningOperations: [],
    failedOperations: [],
    inProgressCount: 0,
    isSelectionRunning: () => false
};

export const BulkContext = createContext<BulkProps | null>(null);

interface BulkProviderProps {
    children: ReactNode;
    shouldSkip?: boolean;
}

// An id stays tracked after its bulk leaves the in-progress list, so BulkHandler still sees the
// terminal summary (and any partial-failure callout keeps rendering) instead of it vanishing the
// moment the in-progress list refreshes. Only those completed ids are capped, FIFO, to bound a
// long-lived page; ids the BFF still reports as in progress are always kept, so a bulk can never
// lose its summary poller to a burst of newer ones.
const MAX_RETAINED_COMPLETED_BULK_IDS = 20;

function trimTrackedIds(prev: readonly string[], inProgressIds: readonly string[]) {
    const isInProgress = new Set(inProgressIds);
    const completed = prev.filter(id => !isInProgress.has(id));
    const next = [...completed.slice(-MAX_RETAINED_COMPLETED_BULK_IDS), ...inProgressIds];

    return next.join(",") === prev.join(",") ? prev : next;
}

function isPolicyStarted(operation: ODataBulkOperation) {
    return operation.source === "automation";
}

export function fromODataBulkOperations(operations: ODataBulkOperation[]): BulkOperationLists {
    // Same BulkOperation.BulkId column: OData exposes it as a long, the remediation contract as a string.
    const inProgressIds = operations
        .filter(operation => operation.status === "inProgress")
        .map(operation => String(operation.bulkId));

    // Review work still counts as in progress and locks its rows; only the Activity panel hides it.
    const panelOperations = operations.filter(operation => !operation.reviewId);

    const runningOperations = panelOperations
        .filter(operation => operation.status === "inProgress")
        .map(operation => ({
            bulkId: String(operation.bulkId),
            action: operation.action,
            isPolicy: isPolicyStarted(operation),
            createdAt: operation.createdAt,
            targetDisplayName: operation.targetDisplayName ?? null
        }));

    const failedOperations = panelOperations.filter(isFailed).map(operation => ({
        bulkId: String(operation.bulkId),
        action: operation.action,
        isPolicy: isPolicyStarted(operation),
        status: operation.status,
        succeededCount: operation.succeededCount,
        failedCount: operation.failedCount,
        errorMessage: operation.errorMessage ?? null
    }));

    return { inProgressIds, runningOperations, failedOperations };
}

const emptyBulkOperationLists: BulkOperationLists = { inProgressIds: [], runningOperations: [], failedOperations: [] };

export function useBulkOperationLists(isEnabled = true): BulkOperationLists {
    const queryClient = useQueryClient();

    // A request still in flight when the gate closes carries the tenant just cleared, and its 403 redirects.
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        return () => {
            void queryClient.cancelQueries({ queryKey: bulkOperationsQueryOptions().queryKey });
        };
    }, [isEnabled, queryClient]);

    const { data: bulkOperationsResponse } = useQuery({ ...bulkOperationsQueryOptions(), enabled: isEnabled });

    return useMemo<BulkOperationLists>(() => {
        // Once the gate closes, show nothing rather than the operations cached while it was open.
        if (!isEnabled) {
            return emptyBulkOperationLists;
        }

        return fromODataBulkOperations(bulkOperationsResponse?.value ?? []);
    }, [isEnabled, bulkOperationsResponse]);
}

export function BulkProvider({ children, shouldSkip = false }: BulkProviderProps) {
    const { inProgressIds, runningOperations, failedOperations } = useBulkOperationLists(!shouldSkip);

    const inProgressIdsKey = inProgressIds.join(",");

    const [trackedIds, setTrackedIds] = useState<readonly string[]>([]);

    useEffect(() => {
        if (shouldSkip) {
            setTrackedIds(prev => (prev.length === 0 ? prev : []));
            return;
        }

        const ids = inProgressIdsKey ? inProgressIdsKey.split(",") : [];

        setTrackedIds(prev => trimTrackedIds(prev, ids));
    }, [inProgressIdsKey, shouldSkip]);

    const summaries = useRemediationBulkSummariesQuery(trackedIds);
    const inProgressCount = inProgressIds.length;

    const { isSelectionRunning } = useRunningSelections(inProgressIdsKey, summaries, shouldSkip);

    const value = useMemo(
        () =>
            shouldSkip
                ? emptyBulkContext
                : { summaries, runningOperations, failedOperations, inProgressCount, isSelectionRunning },
        [shouldSkip, summaries, runningOperations, failedOperations, inProgressCount, isSelectionRunning]
    );

    return <BulkContext.Provider value={value}>{children}</BulkContext.Provider>;
}

export const useBulkContext = () => {
    const context = useContext(BulkContext);
    if (!context) {
        throw new Error("useBulkContext must be used within a BulkProvider");
    }

    return context;
};
